import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Users, Lightbulb, Target, BookOpen, Clock, Zap, Briefcase, Handshake, Leaf, PenTool, GraduationCap, Check, Sparkles } from 'lucide-react';
import EmotionSelector from './EmotionSelector';

// Theme colors - soft, professional palette
const COLORS = {
  primary: '#5DBFAD',
  primarySoft: 'rgba(93, 191, 173, 0.12)',
  primaryMuted: 'rgba(93, 191, 173, 0.6)',
  accent: '#F5A623',
  accentSoft: 'rgba(245, 166, 35, 0.12)',
  coral: '#E8795E',
  coralSoft: 'rgba(232, 121, 94, 0.12)',
  purple: '#9D5CFF',
  purpleSoft: 'rgba(157, 92, 255, 0.12)',
  blue: '#4E9FFF',
  blueSoft: 'rgba(78, 159, 255, 0.12)',
  text: '#2C3E50',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  border: '#E5E7EB',
  background: '#F8FAFB',
  white: '#FFFFFF'
};

// Icon mapping for options
const iconMap = {
  wellness: Heart,
  career: Briefcase,
  team: Users,
  community: Handshake,
  creativity: Lightbulb,
  planet: Leaf,
  learning: BookOpen,
  mentoring: GraduationCap,
  '15min': Zap,
  '30min': Clock,
  '1hr': Clock,
  '2hr': Sparkles,
  helper: Handshake,
  energizer: Zap,
  solver: Target,
  creative: Lightbulb,
  champion: Leaf,
  builder: Users
};

const OnboardingQuest = ({ onComplete, onDismiss }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [totalStars, setTotalStars] = useState(0);
  const [showReward, setShowReward] = useState(false);
  const containerRef = useRef(null);
  
  // User selections
  const [mood, setMood] = useState('');
  const [passions, setPassions] = useState([]);
  const [timeCommitment, setTimeCommitment] = useState('');
  const [avatar, setAvatar] = useState('');

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  // Scroll to top when moving to next question
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Award stars with animation
  const awardStars = (amount, nextStep = true) => {
    setShowReward(true);
    setTotalStars(prev => prev + amount);
    setTimeout(() => {
      setShowReward(false);
      if (nextStep) {
        setCurrentStep(prev => prev + 1);
      }
    }, 1200);
  };

  const [awardedStars, setAwardedStars] = useState(null);
  const [awardResolved, setAwardResolved] = useState(false);

  const runOnboardingCompletion = async () => {
    const finalData = {
      mood, passions, timeCommitment, avatar,
      completedAt: new Date().toISOString(),
    };
    localStorage.setItem('socialstar_onboarding', JSON.stringify(finalData));
    try {
      const result = await onComplete({ ...finalData, action: 'start' });
      const n = Number(result?.stars_awarded);
      setAwardedStars(Number.isFinite(n) && n > 0 ? n : null);
    } catch {
      setAwardedStars(null);
    } finally {
      setAwardResolved(true);
    }
  };

  // Fire the server-side onboarding completion the moment the user
  // arrives at the celebration step. Guarded by awardResolved so the
  // idempotent /users/me/complete-tour endpoint isn't hammered on
  // re-renders.
  React.useEffect(() => {
    if (currentStep === 5 && !awardResolved) {
      runOnboardingCompletion();
    }
    // eslint-disable-next-line
  }, [currentStep]);

  // Dismisses the celebration screen and returns control to App.js,
  // which then unmounts this overlay and renders the main app.
  const handleComplete = () => {
    if (onDismiss) onDismiss();
  };

  // Passion options with soft colors
  const passionOptions = [
    { id: 'wellness', label: 'Personal Wellness', color: COLORS.coralSoft, iconColor: COLORS.coral },
    { id: 'career', label: 'Career Growth', color: COLORS.accentSoft, iconColor: COLORS.accent },
    { id: 'team', label: 'Team Connections', color: COLORS.blueSoft, iconColor: COLORS.blue },
    { id: 'community', label: 'Community Support', color: COLORS.purpleSoft, iconColor: COLORS.purple },
    { id: 'creativity', label: 'Creativity & Ideas', color: COLORS.accentSoft, iconColor: COLORS.accent },
    { id: 'planet', label: 'Sustainability', color: COLORS.primarySoft, iconColor: COLORS.primary },
    { id: 'learning', label: 'Learning & Growth', color: COLORS.coralSoft, iconColor: COLORS.coral },
    { id: 'mentoring', label: 'Mentoring Others', color: COLORS.primarySoft, iconColor: COLORS.primary }
  ];

  // Time options
  const timeOptions = [
    { id: '15min', label: '15 minutes', subtitle: 'per week', color: COLORS.accentSoft, iconColor: COLORS.accent },
    { id: '30min', label: '30 minutes', subtitle: 'per week', color: COLORS.primarySoft, iconColor: COLORS.primary },
    { id: '1hr', label: '1 hour', subtitle: 'per week', color: COLORS.coralSoft, iconColor: COLORS.coral },
    { id: '2hr', label: '2+ hours', subtitle: 'per week', color: COLORS.purpleSoft, iconColor: COLORS.purple }
  ];

  // Avatar/Impact style options
  const avatarOptions = [
    { id: 'helper', label: 'The Supporter', subtitle: 'Help others quietly', color: COLORS.blueSoft, iconColor: COLORS.blue },
    { id: 'energizer', label: 'The Energizer', subtitle: 'Bring the energy', color: COLORS.accentSoft, iconColor: COLORS.accent },
    { id: 'solver', label: 'The Problem Solver', subtitle: 'Fix challenges', color: COLORS.purpleSoft, iconColor: COLORS.purple },
    { id: 'creative', label: 'The Creative', subtitle: 'Think differently', color: COLORS.coralSoft, iconColor: COLORS.coral },
    { id: 'champion', label: 'The Champion', subtitle: 'Lead by example', color: COLORS.primarySoft, iconColor: COLORS.primary },
    { id: 'builder', label: 'The Builder', subtitle: 'Create communities', color: COLORS.coralSoft, iconColor: COLORS.coral }
  ];

  // Option Card Component
  const OptionCard = ({ option, selected, onClick, showSubtitle = false }) => {
    const IconComponent = iconMap[option.id];
    return (
      <motion.button
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className={`option-card ${selected ? 'selected' : ''}`}
        onClick={onClick}
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 16px',
          background: selected ? option.color : COLORS.white,
          border: `2px solid ${selected ? option.iconColor : COLORS.border}`,
          borderRadius: '16px',
          cursor: 'pointer',
          height: '120px',
          width: '100%',
          position: 'relative',
          transition: 'all 0.2s ease',
          boxSizing: 'border-box'
        }}
      >
        {selected && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: option.iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Check size={12} color={COLORS.white} strokeWidth={3} />
          </div>
        )}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          background: selected ? 'rgba(255,255,255,0.8)' : option.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '10px'
        }}>
          {IconComponent && <IconComponent size={22} color={option.iconColor} />}
        </div>
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: COLORS.text,
          textAlign: 'center'
        }}>
          {option.label}
        </div>
        {showSubtitle && option.subtitle && (
          <div style={{
            fontSize: '12px',
            color: COLORS.textSecondary,
            marginTop: '4px',
            textAlign: 'center'
          }}>
            {option.subtitle}
          </div>
        )}
      </motion.button>
    );
  };

  // Display Circle Component
  const DisplayCircle = ({ selectedOption, options, defaultIcon: DefaultIcon }) => {
    const selected = options.find(o => 
      Array.isArray(selectedOption) ? selectedOption.includes(o.id) : o.id === selectedOption
    );
    const IconComponent = selected ? iconMap[selected.id] : DefaultIcon;
    
    return (
      <motion.div
        style={{
          width: '140px',
          height: '140px',
          borderRadius: '50%',
          background: selected ? selected.color : COLORS.primarySoft,
          border: `3px solid ${selected ? selected.iconColor : COLORS.primary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 32px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
        }}
        animate={{
          scale: [1, 1.02, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        {IconComponent && (
          <IconComponent 
            size={56} 
            color={selected ? selected.iconColor : COLORS.primary} 
          />
        )}
      </motion.div>
    );
  };

  return (
    <div 
      className="onboarding-container" 
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: COLORS.background,
        overflowY: 'auto',
        zIndex: 9999
      }}
    >
      {/* Progress Bar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'rgba(0,0,0,0.06)',
        zIndex: 10000
      }}>
        <motion.div
          style={{
            height: '100%',
            background: COLORS.primary,
            borderRadius: '0 2px 2px 0'
          }}
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Reward Animation Overlay */}
      <AnimatePresence>
        {showReward && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.15, 1], rotate: [0, 5, -5, 0] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              <Sparkles size={64} color={COLORS.accent} fill={COLORS.accent} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              style={{
                fontSize: '24px',
                fontWeight: 700,
                color: COLORS.white,
                marginTop: '16px'
              }}
            >
              +10 Stars
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        {/* Step 1: Emotion Selection */}
        {currentStep === 1 && (
          <EmotionSelector
            key="emotion"
            onComplete={(selectedMood) => {
              setMood(selectedMood);
              awardStars(10);
            }}
          />
        )}

        {/* Step 2: Passion Selection */}
        {currentStep === 2 && (
          <motion.div
            key="passions"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            style={{
              maxWidth: '520px',
              margin: '0 auto',
              padding: '60px 20px 120px',
              minHeight: '100vh'
            }}
          >
            <h2 style={{
              fontSize: '26px',
              fontWeight: 700,
              color: COLORS.text,
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              What matters to you?
            </h2>
            <p style={{
              fontSize: '15px',
              color: COLORS.textSecondary,
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              Select all that apply
            </p>

            <DisplayCircle 
              selectedOption={passions} 
              options={passionOptions}
              defaultIcon={Heart}
            />

            {/* Options Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '24px',
              alignItems: 'stretch'
            }}>
              {passionOptions.map((option, index) => (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  style={{ display: 'flex' }}
                >
                  <OptionCard
                    option={option}
                    selected={passions.includes(option.id)}
                    onClick={() => {
                      setPassions(prev =>
                        prev.includes(option.id)
                          ? prev.filter(p => p !== option.id)
                          : [...prev, option.id]
                      );
                    }}
                  />
                </motion.div>
              ))}
            </div>

            {/* Continue Button */}
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '16px 20px 28px',
              background: 'linear-gradient(to top, rgba(248,250,251,1) 70%, rgba(248,250,251,0))'
            }}>
              <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: passions.length > 0 ? 1 : 0.5 }}
                  onClick={() => passions.length > 0 && awardStars(10)}
                  disabled={passions.length === 0}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    background: passions.length > 0 ? COLORS.primary : COLORS.border,
                    color: passions.length > 0 ? COLORS.white : COLORS.textMuted,
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: passions.length > 0 ? 'pointer' : 'not-allowed',
                    boxShadow: passions.length > 0 ? '0 4px 14px rgba(93,191,173,0.3)' : 'none'
                  }}
                >
                  Continue
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Time Commitment */}
        {currentStep === 3 && (
          <motion.div
            key="time"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            style={{
              maxWidth: '520px',
              margin: '0 auto',
              padding: '60px 20px 120px',
              minHeight: '100vh'
            }}
          >
            <h2 style={{
              fontSize: '26px',
              fontWeight: 700,
              color: COLORS.text,
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              How much time can you invest?
            </h2>
            <p style={{
              fontSize: '15px',
              color: COLORS.textSecondary,
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              Weekly commitment
            </p>

            <DisplayCircle 
              selectedOption={timeCommitment} 
              options={timeOptions}
              defaultIcon={Clock}
            />

            {/* Options Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '24px',
              alignItems: 'stretch'
            }}>
              {timeOptions.map((option, index) => (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  style={{ display: 'flex' }}
                >
                  <OptionCard
                    option={option}
                    selected={timeCommitment === option.id}
                    onClick={() => setTimeCommitment(option.id)}
                    showSubtitle={true}
                  />
                </motion.div>
              ))}
            </div>

            {/* Continue Button */}
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '16px 20px 28px',
              background: 'linear-gradient(to top, rgba(248,250,251,1) 70%, rgba(248,250,251,0))'
            }}>
              <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: timeCommitment ? 1 : 0.5 }}
                  onClick={() => timeCommitment && awardStars(10)}
                  disabled={!timeCommitment}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    background: timeCommitment ? COLORS.primary : COLORS.border,
                    color: timeCommitment ? COLORS.white : COLORS.textMuted,
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: timeCommitment ? 'pointer' : 'not-allowed',
                    boxShadow: timeCommitment ? '0 4px 14px rgba(93,191,173,0.3)' : 'none'
                  }}
                >
                  Continue
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Impact Style */}
        {currentStep === 4 && (
          <motion.div
            key="avatar"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            style={{
              maxWidth: '520px',
              margin: '0 auto',
              padding: '60px 20px 120px',
              minHeight: '100vh'
            }}
          >
            <h2 style={{
              fontSize: '26px',
              fontWeight: 700,
              color: COLORS.text,
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              Choose your impact style
            </h2>
            <p style={{
              fontSize: '15px',
              color: COLORS.textSecondary,
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              How do you make a difference?
            </p>

            <DisplayCircle 
              selectedOption={avatar} 
              options={avatarOptions}
              defaultIcon={Target}
            />

            {/* Options Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '24px',
              alignItems: 'stretch'
            }}>
              {avatarOptions.map((option, index) => (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  style={{ display: 'flex' }}
                >
                  <OptionCard
                    option={option}
                    selected={avatar === option.id}
                    onClick={() => setAvatar(option.id)}
                    showSubtitle={true}
                  />
                </motion.div>
              ))}
            </div>

            {/* Continue Button */}
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '16px 20px 28px',
              background: 'linear-gradient(to top, rgba(248,250,251,1) 70%, rgba(248,250,251,0))'
            }}>
              <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: avatar ? 1 : 0.5 }}
                  onClick={() => avatar && awardStars(10)}
                  disabled={!avatar}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    background: avatar ? COLORS.primary : COLORS.border,
                    color: avatar ? COLORS.white : COLORS.textMuted,
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: avatar ? 'pointer' : 'not-allowed',
                    boxShadow: avatar ? '0 4px 14px rgba(93,191,173,0.3)' : 'none'
                  }}
                >
                  Continue
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 5: Completion */}
        {currentStep === 5 && (
          <motion.div
            key="completion"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              maxWidth: '480px',
              margin: '0 auto',
              padding: '80px 20px',
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 15, delay: 0.2 }}
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: COLORS.accentSoft,
                border: `3px solid ${COLORS.accent}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px'
              }}
            >
              <Sparkles size={48} color={COLORS.accent} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              style={{
                fontSize: '28px',
                fontWeight: 700,
                color: COLORS.text,
                marginBottom: '8px',
                textAlign: 'center'
              }}
            >
              You're all set!
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              style={{
                fontSize: '16px',
                color: COLORS.textSecondary,
                marginBottom: '32px',
                textAlign: 'center'
              }}
            >
              You've earned your first stars
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              style={{
                background: COLORS.accentSoft,
                border: `2px solid ${COLORS.accent}`,
                borderRadius: '20px',
                padding: '24px 48px',
                marginBottom: '40px',
                textAlign: 'center'
              }}
            >
              {/* Only show a star count if the SERVER confirmed one. If
                  the call hasn't resolved yet, or resolved with 0 (idempotent
                  no-op because the user was already awarded), or failed —
                  show a neutral completion message instead. Never display
                  an unconfirmed number. */}
              {awardResolved && awardedStars && awardedStars > 0 ? (
                <>
                  <div data-testid="onboarding-awarded-stars" style={{
                    fontSize: '42px', fontWeight: 800, color: COLORS.accent, marginBottom: '4px'
                  }}>
                    +{awardedStars}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400E' }}>
                    Stars Earned
                  </div>
                </>
              ) : (
                <div data-testid="onboarding-generic-message" style={{
                  fontSize: '16px', fontWeight: 600, color: '#92400E', padding: '6px 0'
                }}>
                  {awardResolved
                    ? "You're all set!"
                    : 'Saving your onboarding...'}
                </div>
              )}
            </motion.div>

            <motion.button
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              onClick={handleComplete}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                padding: '16px 48px',
                background: COLORS.primary,
                color: COLORS.white,
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(93,191,173,0.3)'
              }}
            >
              Let's get started
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingQuest;
